// ─── PRESENCE / ACTIVE STATUS ───────────────────────────────────────────────
// Single source of truth for "is this user actually online right now?".
//
// `profiles.is_online` alone is NOT trustworthy: it's set true by a 60s
// heartbeat and set false on visibilitychange/pagehide/unload — but those
// never fire when a tab is force-quit, a phone kills the PWA, the network
// drops, or the device sleeps. Those users stay `is_online = true` forever and
// show as active days later.
//
// So presence is derived: online === flag set AND the heartbeat is recent.
// The heartbeat runs every 60s, so a 3-minute window tolerates two missed
// beats before we call someone offline.

export const PRESENCE_STALE_MS = 3 * 60 * 1000; // 3 minutes

/**
 * True when a profile should render as online/active.
 * @param {object} p profile row (needs is_online, last_seen, hide_online_status)
 * @param {number} staleMs override the staleness window
 */
export function isUserOnline(p, staleMs = PRESENCE_STALE_MS) {
  if (!p?.is_online) return false;
  if (p.hide_online_status) return false; // the user chose to appear offline
  if (!p.last_seen) return false;         // never heartbeat'd → not online
  const elapsed = Date.now() - new Date(p.last_seen).getTime();
  if (!Number.isFinite(elapsed)) return false;
  return elapsed < staleMs;
}

/** Human-readable last-active label for offline users ("3m ago", "2h ago"). */
export function lastActiveLabel(p) {
  if (!p?.last_seen) return 'Offline';
  const elapsed = Date.now() - new Date(p.last_seen).getTime();
  if (!Number.isFinite(elapsed) || elapsed < 0) return 'Offline';
  const mins = Math.floor(elapsed / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return days === 1 ? 'Yesterday' : `${days}d ago`;
}
